import React from 'react';

import PersonRow from 'components/CallScribe/Conference/PersonRow';
import usePeopleListStyles, { TPeopleListSurface } from 'components/CallScribe/Conference/peopleListStyles';
import { IPeopleGroup, IPerson } from 'components/CallScribe/Conference/usePeopleDirectory';

export const personOptionId = (listId: string, key: string): string =>
  `${listId}-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`;

export const flattenGroups = (groups: IPeopleGroup[]): IPerson[] => groups.flatMap(g => g.people);

export const stepActive = (people: IPerson[], activeKey: string | null, key: string): string | null => {
  if (people.length === 0) return null;
  const at = activeKey === null ? -1 : people.findIndex(p => p.key === activeKey);
  if (key === 'Home') return people[0].key;
  if (key === 'End') return people[people.length - 1].key;
  if (key === 'ArrowDown') return people[Math.min(people.length - 1, at + 1)].key;
  if (key === 'ArrowUp') return at <= 0 ? people[0].key : people[at - 1].key;
  return activeKey;
};

export interface IPeopleListProps {
  listId: string;
  label: string;
  groups: IPeopleGroup[];
  query: string;
  showCounts: boolean;
  activeKey: string | null;
  selectedKey: string | null;
  surface?: TPeopleListSurface;
  focusable?: boolean;
  onPick: (person: IPerson) => void;
  onActiveChange: (key: string | null) => void;
  onConfirm?: () => void;
  onTypeKey?: (key: string) => void;
}

const PeopleList: React.FC<IPeopleListProps> = ({
  listId,
  label,
  groups,
  query,
  showCounts,
  activeKey,
  selectedKey,
  surface = 'bg',
  focusable = false,
  onPick,
  onActiveChange,
  onConfirm,
  onTypeKey,
}) => {
  const classes = usePeopleListStyles();
  const people = flattenGroups(groups);
  const active = people.find(p => p.key === activeKey) ?? null;

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      onActiveChange(stepActive(people, activeKey, e.key));
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (active && active.key === selectedKey) onConfirm?.();
      else if (active) onPick(active);
      return;
    }
    if (/^[0-9+*#]$/.test(e.key) && onTypeKey) {
      e.preventDefault();
      onTypeKey(e.key);
    }
  };

  return (
    <div
      id={listId}
      role="listbox"
      aria-label={label}
      tabIndex={focusable ? 0 : -1}
      aria-activedescendant={active ? personOptionId(listId, active.key) : undefined}
      className={classes.list}
      onKeyDown={focusable ? onKeyDown : undefined}
      onFocus={() => {
        if (focusable && !active && people.length > 0) onActiveChange(people[0].key);
      }}
    >
      {groups.map(group => (
        <div key={group.key} role="group" aria-label={group.label} className={classes.group}>
          <div
            aria-hidden
            className={`${classes.header} ${surface === 'surface' ? classes.headerOnSurface : classes.headerOnBg}`}
          >
            <span>{group.label}</span>
            {showCounts ? <span className={classes.headerCount}>{group.people.length}</span> : null}
          </div>
          {group.people.map(person => (
            <PersonRow
              key={person.key}
              optionId={personOptionId(listId, person.key)}
              person={person}
              query={query}
              active={person.key === activeKey}
              selected={person.key === selectedKey}
              onPick={p => {
                onActiveChange(p.key);
                onPick(p);
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default PeopleList;
